import {Injectable} from "@angular/core";
import {Patient} from "../../clinical/patient/models/patient";
import * as _ from "lodash";

export interface IPatientUtilsStudyItem {
	studyDescription: string;
	statusKey: number;
	spsCodeKey: number;
	spsId: number;
}

@Injectable({ providedIn: 'root' })
  export class StudyUtilsService {
	constructor() { }

	/**
	 * Returns the most relevant study status.
	 * @param patient.
	 * @returns Status.
	 */
	public getDeepestStatus(patient: Patient): string[] {
		let status = [];
		if (this.patientObjectContainsPps(patient)) {
			this.getStudyStatusFromPps(patient, status);
		} else if (this.patientObjectContainsSps(patient)) {
			this.getStudyStatusFromSps(patient, status);
		} else if (this.patientObjectContainsOrder(patient)) {
			this.getStudyStatusFromOrder(patient, status);
		}
		return status;
	}

	/**
	 *
	 * @param {Patient} patient
	 * @returns {IPatientUtilsStudyItem[]}
	 */
	public getAllStudyItems(patient: Patient): IPatientUtilsStudyItem[] {
		// todo - what happens in cases where we have an sps and it is removed and
		// we are left with the ordered procedures. how does it affects the relevant features?
		if (this.notEmptyArray(patient.firstVisit.firstOrder.requestedProcedure)){
			let studyItems: IPatientUtilsStudyItem[] = [];
			patient.firstVisit.firstOrder.requestedProcedure.forEach(rp => {
				if (this.notEmptyArray(rp.sps)) {
					rp.sps.forEach(sps => {
						if (this.notEmptyArray(sps.spsXPps)) {
							sps.spsXPps.forEach(x => {
								if (this.notEmptyArray(x.pps)) {
									x.pps.forEach(pps => {
										studyItems.push({
											studyDescription: pps.FKLookupRows.ppsCodeKey.description,
											statusKey: pps.statusKey,
											spsCodeKey: pps.ppsCodeKey,
											spsId: sps.spsId
										});
									});
								}
							});
						} else {
							studyItems.push({
								studyDescription: sps.FKLookupRows.spsCodeKey.description,
								statusKey: sps.statusKey,
								spsCodeKey: sps.spsCodeKey,
								spsId: sps.spsId
							});
						}
					});
				}
			});
			if (studyItems.length) {
				return studyItems;
			}
		}
		return this.getStudyItemFromOrder(patient);
	}

	/**
	 * Goes through the patient object and searches for study description at order level.
	 * @param {Patient} patient
	 * @param studyItem
	 */
	public getStudyItemFromOrder(patient: Patient) {
		let studyItems: IPatientUtilsStudyItem[] = [];

		if (this.notEmptyArray(patient.visit)) {
			patient.visit.forEach(v => {
				if (this.notEmptyArray(v.orders)) {
					v.orders.forEach(o => {
						if (this.notEmptyArray(o.orderedProcedureItem)) {
							o.orderedProcedureItem.forEach(opi => {
								let item: IPatientUtilsStudyItem = {
									studyDescription: null,
									statusKey: o.statusKey,
									spsCodeKey: opi.spsCodeKey,
									spsId: null
								};

								if (opi.FKLookupRows && opi.FKLookupRows.spsCodeKey && opi.FKLookupRows.spsCodeKey.description) {
									item.studyDescription = opi.FKLookupRows.spsCodeKey.description;
								}

								if (_.isArray(studyItems) && !studyItems.find(i => i["spsCodeKey"] === item.spsCodeKey)) {
									studyItems.push(item);
								}
							});
						}
					});
				}
			});
		}
		return studyItems;
	}

	/**
	 * Goes through the patient object and searches for study description at sps level.
	 * Depending on the description parameter (string or array), a single study description
	 * or descriptions for all studies at sps level will be returned.
	 * @param patient.
	 * @param studyItem.
	 * @param getCurrentActualStatusKey determines if the study status shall be retrieved from the previous step if missing.
	 */
	public getStudyItemFromSps(patient: Patient, studyItem: any, getCurrentActualStatusKey: boolean) {
		if (this.notEmptyArray(patient.visit)) {
			patient.visit.forEach(v => {
				if (this.notEmptyArray(v.orders)) {
					v.orders.forEach(o => {
						if (this.notEmptyArray(o.requestedProcedure)) {
							o.requestedProcedure.forEach(rp => {
								if (this.notEmptyArray(rp.sps)) {
									rp.sps.forEach(sps => {
										let item: IPatientUtilsStudyItem = {
											studyDescription: null,
											statusKey: sps.statusKey,
											spsCodeKey: sps.spsCodeKey,
											spsId: sps.spsId
										};

										if (sps.FKLookupRows && sps.FKLookupRows.spsCodeKey && sps.FKLookupRows.spsCodeKey.description) {
											item.studyDescription = sps.FKLookupRows.spsCodeKey.description;
										}

										if (!sps.statusKey && getCurrentActualStatusKey) {
											item.statusKey = o.statusKey;
										}

										if (_.isArray(studyItem) && !studyItem.find(i => i["spsCodeKey"] === item.spsCodeKey)) {
											studyItem.push(item);
										} else if (!_.isArray(studyItem)) {
											studyItem = !studyItem ? item : studyItem;
										}
									});
								}
							});
						}
					});
				}
			});
		}
	}

	/**
	 * Goes through the patient object and searches for study description at pps level.
	 * Depending on the description parameter (string or array), a single study description
	 * or descriptions for all studies at sps level will be returned.
	 * @param patient.
	 * @param studyItem.
	 * @param getCurrentActualStatusKey determines if the study status shall be retrieved from the previous step if missing.
	 */
	public getStudyItemFromPps(patient: Patient, studyItem: any, getCurrentActualStatusKey: boolean) {
		if (this.notEmptyArray(patient.visit)) {
			patient.visit.forEach(v => {
				if (this.notEmptyArray(v.orders)) {
					v.orders.forEach(o => {
						if (this.notEmptyArray(o.requestedProcedure)) {
							o.requestedProcedure.forEach(rp => {
								if (this.notEmptyArray(rp.sps)) {
									rp.sps.forEach(sps => {
										if (this.notEmptyArray(sps.spsXPps)) {
											sps.spsXPps.forEach(sxp => {
												if (this.notEmptyArray(sxp.pps)) {
													sxp.pps.forEach(pps => {
														let item: IPatientUtilsStudyItem = {
															studyDescription: null,
															statusKey: pps.statusKey,
															spsCodeKey: pps.ppsCodeKey,
															spsId: sps.spsId
														};

														if (pps.FKLookupRows && pps.FKLookupRows.ppsCodeKey && pps.FKLookupRows.ppsCodeKey.description) {
															item.studyDescription = pps.FKLookupRows.ppsCodeKey.description;
														}

														if (!pps.statusKey && getCurrentActualStatusKey) {
															item.statusKey = sps.statusKey ? sps.statusKey : o.statusKey;
														}

														if (_.isArray(studyItem) && !studyItem.find(i => i["spsCodeKey"] === item.spsCodeKey)) {
															studyItem.push(item);
														} else if (!_.isArray(studyItem)) {
															studyItem = !studyItem ? item : studyItem;
														}
													});
												}
											});
										}
									});
								}
							});
						}
					});
				}
			});
		}
	}

	/**
	 * Goes through the patient object and searches for study status at order level.
	 * Depending on the status parameter (string or array), a single study status
	 * or statuses for all studies at order level will be returned.
	 * @param {Patient} patient
	 * @param status
	 */
	public getStudyStatusFromOrder(patient: Patient, status: string[]) {
		if (this.notEmptyArray(patient.visit)) {
			patient.visit.forEach(v => {
				if (this.notEmptyArray(v.orders)) {
					v.orders.forEach(o => {
						if (o.FKLookupRows && o.FKLookupRows.statusKey && o.FKLookupRows.statusKey.description) {
							if (_.indexOf(status, o.FKLookupRows.statusKey.description) < 0) {
								status.push(o.FKLookupRows.statusKey.description);
							}
						}
					});
				}
			});
		}
	}

	/**
	 * Goes through the patient object and searches for study status at sps level.
	 * Depending on the status parameter (string or array), a single study status
	 * or statuses for all studies at sps level will be returned.
	 * @param {Patient} patient
	 * @param status
	 */
	public getStudyStatusFromSps(patient: Patient, status: string[]) {
		if (this.notEmptyArray(patient.visit)) {
			patient.visit.forEach(v => {
				if (this.notEmptyArray(v.orders)) {
					v.orders.forEach(o => {
						if (this.notEmptyArray(o.requestedProcedure)) {
							o.requestedProcedure.forEach(rp => {
								if (this.notEmptyArray(rp.sps)) {
									rp.sps.forEach(sps => {
										if (sps.FKLookupRows && sps.FKLookupRows.statusKey && sps.FKLookupRows.statusKey.description) {
											if (_.indexOf(status, sps.FKLookupRows.statusKey.description) < 0) {
												status.push(sps.FKLookupRows.statusKey.description);
											}
										}
									});
								}
							});
						}
					});
				}
			});
		}

		if (!status.length) {
			this.getStudyStatusFromOrder(patient, status);
		}
	}

	/**
	 * Goes through the patient object and searches for study status at pps level.
	 * Depending on the status parameter (string or array), a single study status
	 * or statuses for all studies at pps level will be returned.
	 * @param {Patient} patient
	 * @param status
	 */
	public getStudyStatusFromPps(patient: Patient, status: string[]) {
		if (this.notEmptyArray(patient.visit)) {
			patient.visit.forEach(v => {
				if (this.notEmptyArray(v.orders)) {
					v.orders.forEach(o => {
						if (this.notEmptyArray(o.requestedProcedure)) {
							o.requestedProcedure.forEach(rp => {
								if (this.notEmptyArray(rp.sps)) {
									rp.sps.forEach(sps => {
										if (this.notEmptyArray(sps.spsXPps)) {
											sps.spsXPps.forEach(sxp => {
												if (this.notEmptyArray(sxp.pps)) {
													sxp.pps.forEach(pps => {
														if (pps.FKLookupRows && pps.FKLookupRows.statusKey && pps.FKLookupRows.statusKey.description) {
															if (_.indexOf(status, pps.FKLookupRows.statusKey.description) < 0) {
																status.push(pps.FKLookupRows.statusKey.description);
															}
														}
													});
												}
											});
										}
									});
								}
							});
						}
					});
				}
			});
		}

		if (!status.length) {
			this.getStudyStatusFromSps(patient, status);
		}
	}

	/**
	 * Returns PACS accession number.
	 * @param {Patient} patient.
	 * @returns {string} PACS accession number.
	 */
	public getAccessionNumber(patient: Patient): string {
		let number;
		if (this.patientObjectContainsSps(patient)) {
			if (this.notEmptyArray(patient.visit)) {
				patient.visit.forEach(v => {
					if (this.notEmptyArray(v.orders)) {
						v.orders.forEach(o => {
							if (this.notEmptyArray(o.requestedProcedure)) {
								o.requestedProcedure.forEach(rp => {
									if (this.notEmptyArray(rp.sps)) {
										rp.sps.forEach(sps => {
											number = !number && sps.pacsSpsId ? sps.pacsSpsId : number;
										});
									}
								});
							}
						});
					}
				});
			}
		}
		return number;
	}

	/**
	 * Checks if patient object contains order.
	 * @param patient object.
	 * @returns {boolean} True if contains, false otherwise.
	 */
	public patientObjectContainsOrder(patient: Patient): boolean {
		let levels = ['visit', 'orders'];
		return this.contains(patient, levels);
	}

	/**
	 * Checks if patient object contains sps.
	 * @param patient object.
	 * @returns {boolean} True if contains, false otherwise.
	 */
	public patientObjectContainsSps(patient: Patient): boolean {
		let contains = false;
		if (!this.patientObjectContainsOrder(patient)) {
			return contains;
		} else {
			let levels = ['visit', 'orders', 'requestedProcedure', 'sps'];
			contains = this.contains(patient, levels);
		}
		return contains;
	}

	/**
	 * Checks if patient object contains pps.
	 * @param patient object.
	 * @returns {boolean} True if contains, false otherwise.
	 */
	public patientObjectContainsPps(patient: Patient): boolean {
		let contains = false;
		if (!this.patientObjectContainsSps(patient)) {
			return contains;
		} else {
			let levels = ['visit', 'orders', 'requestedProcedure', 'sps', 'spsXPps', 'pps'];
			contains = this.contains(patient, levels);
		}
		return contains;
	}

	/**
	 *
	 * @param model
	 * @param levels
	 * @param {number} levelIndex
	 * @returns {boolean}
	 */
	protected contains(model, levels, levelIndex = 0) {
		if (levels.length === levelIndex) {
			return true;
		}

		if (this.notEmptyArray(model[levels[levelIndex]])) {
			for (let i = 0 ; i < model[levels[levelIndex]].length; i++) {
				if (this.contains(model[levels[levelIndex]][i], levels, levelIndex + 1)) {
					return true;
				}
			}
		}

		return false;
	}

	/**
	 * Checks if a current array is empty or contains elements
	 * @param array to check
	 * @returns true if contains
	 */
	protected notEmptyArray(arr: Array<any>) {
		return arr && arr.length;
	}


	/**
	 * Returns all relevant study descriptions.
	 * @param patient.
	 * @param getCurrentActualStatusKey determines if the study status shall be retrieved from the previous step if missing.
	 */
	public getAllDeepestStudiesDescriptions(patient: Patient, getCurrentActualStatusKey: boolean): string[] {
		let studyItems: IPatientUtilsStudyItem[] = [];
		if (this.patientObjectContainsPps(patient)) {
			this.getStudyItemFromPps(patient, studyItems, getCurrentActualStatusKey);
		} else if (this.patientObjectContainsSps(patient)) {
			this.getStudyItemFromSps(patient, studyItems, getCurrentActualStatusKey);
		} else if (this.patientObjectContainsOrder(patient)) {
			studyItems = this.getStudyItemFromOrder(patient);
		}
		return studyItems.map(item => {
			return item.studyDescription;
		});
	}

	// /**
	//  * Returns all relevant study statuses.
	//  * @param {Patient} patient
	//  * @returns {string[]}
	//  */
	// public getAllDistinctDeepestStatuses(patient: Patient): string[] {
	// 	let status: string[] = [];
	// 	if (this.patientObjectContainsPps(patient)) {
	// 		this.getStudyStatusFromPps(patient, status);
	// 	} else if (this.patientObjectContainsSps(patient)) {
	// 		this.getStudyStatusFromSps(patient, status);
	// 	} else if (this.patientObjectContainsOrder(patient)) {
	// 		this.getStudyStatusFromOrder(patient, status);
	// 	}
	// 	return status;
	// }
	//
	// /**
	//  * Returns all PACS accession numbers.
	//  * @param {Patient} patient
	//  * @returns {string[]}
	//  */
	// public getAllDistinctAccessionNumbers(patient: Patient): string[] {
	// 	let numbers = [];
	// 	if (this.patientObjectContainsSps(patient)) {
	// 		if (this.notEmptyArray(patient.visit)) {
	// 			patient.visit.forEach(v => {
	// 				if (this.notEmptyArray(v.orders)) {
	// 					v.orders.forEach(o => {
	// 						if (this.notEmptyArray(o.requestedProcedure)) {
	// 							o.requestedProcedure.forEach(rp => {
	// 								if (this.notEmptyArray(rp.sps)) {
	// 									rp.sps.forEach(sps => {
	// 										if (sps.pacsSpsId && _.indexOf(numbers, sps.pacsSpsId) < 0) {
	// 											numbers.push(sps.pacsSpsId);
	// 										}
	// 									});
	// 								}
	// 							});
	// 						}
	// 					});
	// 				}
	// 			});
	// 		}
	// 	}
	// 	return numbers;
	// }

	// /**
	//  * Returns all relevant study descriptions.
	//  * @param patient.
	//  * @param getCurrentActualStatusKey determines if the study status shall be retrieved from the previous step if missing.
	//  */
	// public getAllDeepestStudies(patient: Patient, getCurrentActualStatusKey: boolean): IPatientUtilsStudyItem[] {
	// 	let studyItems: IPatientUtilsStudyItem[] = [];
	// 	if (this.patientObjectContainsPps(patient)) {
	// 		this.getStudyItemFromPps(patient, studyItems, getCurrentActualStatusKey);
	// 	} else if (this.patientObjectContainsSps(patient)) {
	// 		this.getStudyItemFromSps(patient, studyItems, getCurrentActualStatusKey);
	// 	} else if (this.patientObjectContainsOrder(patient)) {
	// 		this.getStudyItemFromOrder(patient, studyItems);
	// 	}
	// 	return studyItems;
	// }

}
